<template>
    <div v-if="series && series.projects">
        <ProjectList
            :loading="loading"
            :is-empty="isEmpty"
            :loading-message="$t('loading-projects')"
            :empy-state-description="$t('no-projects')"
            :create-disabled-explanation="createProjectDisabledExplanation"
            :create-link="getCreateProjectLink(series.id, series.nameLong)"
            :create-label="createProjectButtonLabel"
        >
            <CollapsibleHeadline
                v-for="[grouping, periodProjects] in groupedProjects"
                :key="grouping"
                :heading-level="3"
                :headline-id="grouping"
                :initial-is-open="['mifri', 'future'].includes(grouping)"
            >
                <template #headline>
                    {{ $t('project-grouping-label', { grouping: grouping, count: periodProjects.length }) }}
                </template>
                <template #content>
                    <div v-if="periodProjects.length > 0" class="space-y-2">
                        <ProjectCard
                            v-for="project in periodProjects"
                            :key="project.id"
                            :link-target="getProjectLink(project.id)"
                            :link-text="project.nameShort"
                            :status-badge-variant="projectStatusToBadgeVariant(project.status)"
                            :status-badge-label="getProjectStatusLabel(project.status)"
                        ></ProjectCard>
                    </div>
                    <div v-else class="space-y-2 font-light text-gray-600">
                        ({{ $t('no-projects-in-this-period') }})
                    </div>
                </template>
            </CollapsibleHeadline>
        </ProjectList>
    </div>
</template>

<script setup lang="ts">
import auth from '@/auth/auth';
import CollapsibleHeadline from '@/components/CollapsibleHeadline/CollapsibleHeadline.vue';
import { $t } from '@/plugins/fluent';
import ProjectCard from '@/project/components/ProjectCard.vue';
import ProjectList from '@/project/components/ProjectList.vue';
import { useProjectSeriesInjection } from '@/project/composables/useProjectSeriesInjection';
import {
    getCreateProjectLink,
    getProjectLink,
    getProjectStatusLabel,
    projectStatusToBadgeVariant,
} from '@/project/project';
import { useHeadSafe } from '@unhead/vue';
import { DateTime } from 'luxon';
import { computed } from 'vue';

const { series, loading } = useProjectSeriesInjection();
const projects = computed(() => series.value?.projects?.nodes ?? []);
const isEmpty = computed(() => series.value?.projects?.nodes.length === 0);

const now = DateTime.now();
const currentYear = now.year;
const inFiveYears = now.plus({ years: 5 }).year;

const groupingPeriods = ['past', 'mifri', 'future'] as const;

const groupedProjects = computed(() => {
    const groupedObjects = Object.groupBy(projects.value, (project) => {
        if (project.executionYear < currentYear) {
            return 'past';
        }
        if (project.executionYear > inFiveYears) {
            return 'future';
        }
        return 'mifri';
    });
    const periodMap = new Map();
    for (const period of groupingPeriods) {
        const sortedObjects =
            groupedObjects[period]?.toSorted(
                (a, b) => a.executionYear - b.executionYear || a.nameShort.localeCompare(b.nameShort),
            ) ?? [];
        periodMap.set(period, sortedObjects);
    }
    return periodMap;
});

useHeadSafe({ title: () => `${$t('overview')} - ${series.value?.nameShort} - ${$t('project-wiki')}` });

const createProjectDisabledExplanation = computed(() => {
    if (!auth.isAdmin()) {
        return $t('no-permission-to-create');
    }
    if (
        series.value &&
        series.value.singleProject &&
        series.value.projects &&
        series.value.projects.nodes.length >= 1
    ) {
        return $t('single-project-limit-reached');
    }
    return undefined;
});

const createProjectButtonLabel = computed(() =>
    series.value?.singleProject ? $t('single-project-create') : $t('project-create'),
);
</script>
